import React, { useState, useEffect } from 'react';
import useTrainingPlanStore from '../../stores/trainingPlanStore';
import { ChevronDown, ChevronUp } from 'lucide-react';

export default function AdvancedPlanSettings() {
 const meta = useTrainingPlanStore(state => state.meta);
 const assignment = useTrainingPlanStore(state => state.assignment);
 const updateMeta = useTrainingPlanStore(state => state.updateMeta);
 const updateAssignment = useTrainingPlanStore(state => state.updateAssignment);
 const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
 const [isOpen, setIsOpen] = useState(false);

 useEffect(() => {
 const handleStorageChange = () => setLanguage(localStorage.getItem('language') || 'english');
 window.addEventListener('storage', handleStorageChange);
 window.addEventListener('languageChange', handleStorageChange);
 return () => {
 window.removeEventListener('storage', handleStorageChange);
 window.removeEventListener('languageChange', handleStorageChange);
 };
 }, []);

 const [localNotes, setLocalNotes] = useState('');
 const [localFocus, setLocalFocus] = useState('');

 useEffect(() => {
 setLocalNotes(meta.pre_workout_notes || '');
 setLocalFocus(meta.focus || '');
 }, [meta.pre_workout_notes, meta.focus]);

  const handleBlur = (field, localValue) => {
    if (localValue !== meta[field]) {
      updateMeta({ [field]: localValue });
    }
  };

 return (
 <div className="pt-4 border-t border-line dark:border-line-dark">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-sm font-semibold text-ink dark:text-ink-dark hover:text-forest transition-colors py-2"
      >
 <span>{language === 'hebrew' ? 'הגדרות מתקדמות' : 'Advanced Settings'}</span>
 {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
 </button>
 
 {isOpen && (
 <div className="mt-4 flex flex-col gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-1.5">
 <label htmlFor="plan-status" className="text-xs font-semibold text-ink-muted uppercase tracking-wider">{language === 'hebrew' ? 'סטטוס' : 'Status'}</label>
 <select
 id="plan-status"
 value={assignment.status}
 onChange={(e) => updateAssignment({ status: e.target.value })}
 className="w-full min-h-11 px-3 py-2 rounded-md border bg-canvas dark:bg-canvas-dark text-ink dark:text-ink-dark outline-none focus:ring-2 border-line dark:border-line-dark focus:border-forest focus:ring-forest font-medium"
 >
 <option value="active">{language === 'hebrew' ? 'פעיל' : 'Active'}</option>
 <option value="paused">{language === 'hebrew' ? 'מושהה' : 'Paused'}</option>
 <option value="completed">{language === 'hebrew' ? 'הושלם' : 'Completed'}</option>
 <option value="archived">{language === 'hebrew' ? 'בארכיון' : 'Archived'}</option>
 </select>
 </div>

 <div className="space-y-1.5">
 <label className="text-xs font-semibold text-ink-muted uppercase tracking-wider">{language === 'hebrew' ? 'רמת עצימות' : 'Intensity'}</label>
 <select 
 value={meta.intensity_level}
 onChange={(e) => updateMeta({ intensity_level: e.target.value })}
 className={`w-full px-3 py-2 rounded-md border bg-canvas dark:bg-canvas-dark text-ink dark:text-ink-dark outline-none focus:ring-2 border-line dark:border-line-dark focus:border-forest focus:ring-forest font-medium`}
 >
 <option value="low">{language === 'hebrew' ? 'נמוך' : 'Low'}</option>
 <option value="moderate">{language === 'hebrew' ? 'בינוני' : 'Moderate'}</option>
 <option value="moderate-high">{language === 'hebrew' ? 'בינוני-גבוה' : 'Moderate-High'}</option>
 <option value="high">{language === 'hebrew' ? 'גבוה' : 'High'}</option>
 </select>
 </div>
 </div>

 <div className="space-y-1.5">
 <label className="text-xs font-semibold text-ink-muted uppercase tracking-wider">{language === 'hebrew' ? 'מיקוד' : 'Focus'}</label>
 <input 
 type="text" 
 value={localFocus} 
 onChange={(e) => setLocalFocus(e.target.value)}
 onBlur={() => handleBlur('focus', localFocus)}
 onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
 className={`w-full px-3 py-2 rounded-md border bg-canvas dark:bg-canvas-dark text-ink dark:text-ink-dark outline-none focus:ring-2 border-line dark:border-line-dark focus:border-forest focus:ring-forest font-medium`}
 />
 <p className="text-[10px] text-ink-muted font-medium">{language === 'hebrew' ? 'למשל: פרוגרסיב אוברלואד' : 'e.g. Progressive Overload'}</p>
 </div>

        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-ink-muted uppercase tracking-wider">{language === 'hebrew' ? 'הערות חימום / כללי' : 'Pre-Workout Notes'}</label>
          <textarea 
            value={localNotes}
            onChange={(e) => setLocalNotes(e.target.value)}
            onBlur={() => handleBlur('pre_workout_notes', localNotes)}
            placeholder={language === 'hebrew' ? 'למשל: חימום דינמי 5 דקות...' : 'e.g. Standard dynamic warmup...'}
            className={`w-full px-3 py-2 rounded-md border bg-canvas dark:bg-canvas-dark text-ink dark:text-ink-dark outline-none focus:ring-2 border-line dark:border-line-dark focus:border-forest focus:ring-forest text-sm resize-none`}
            rows={2}
          />
        </div>
      </div>
      )}
    </div>
  );
}
